import { SDKError } from '../errors.js';
import type { HttpLayer } from '../http.js';
import type {
  ListMediaParams,
  ListMediaResponse,
  MediaBulkParams,
  MediaBulkResult,
  MediaContentParams,
  MediaObject,
  MediaRestoreResult,
  MediaUploadParams,
  MediaUsage,
} from '../types/media.js';

/**
 * Account-global versioned artifacts. List and get are tenant-scoped to the
 * caller; a miss is 404, never 403.
 */
export class MediaResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/media';

  /** Living (or trashed) page for the caller account. */
  async list(params?: ListMediaParams): Promise<ListMediaResponse> {
    return this.http.get(this.prefix, params);
  }

  /** One object by id. Unknown or cross-account ids are 404. */
  async get(mediaId: string): Promise<MediaObject> {
    return this.http.get(`${this.prefix}/${mediaId}`);
  }

  /**
   * Occupancy for an account. Omit `accountId` for the caller. Any org member
   * may name another same-org account (`?account_id=`).
   */
  async usage(accountId?: string): Promise<MediaUsage> {
    return this.http.get(
      `${this.prefix}/usage`,
      accountId !== undefined ? { account_id: accountId } : undefined,
    );
  }

  /**
   * Move many artifacts to Trash, undelete them, or purge them. Completion is a
   * product Live Feed notice (`poster_kind=system`).
   */
  async bulk(params: MediaBulkParams): Promise<MediaBulkResult> {
    if (params.ids.length === 0) {
      throw new SDKError('bulk: ids must name at least one artifact');
    }
    return this.http.post(`${this.prefix}/bulk`, {
      action: params.action,
      ids: params.ids,
    });
  }

  /** Relative content URL for a format (default latest svg). */
  contentUrl(mediaId: string, params?: MediaContentParams): string {
    const query = new URLSearchParams();
    if (params?.format) query.set('format', params.format);
    if (params?.version !== undefined) query.set('version', String(params.version));
    const qs = query.toString();
    return qs ? `${this.prefix}/${mediaId}/content?${qs}` : `${this.prefix}/${mediaId}/content`;
  }

  /** Live-checked bytes. No version = latest. Living path 404s for Trash. */
  async content(mediaId: string, params?: MediaContentParams): Promise<ArrayBuffer> {
    return this.http.get(`${this.prefix}/${mediaId}/content`, params, { acceptBinary: true });
  }

  /** Roll a prior version forward as a new latest. */
  async restore(mediaId: string, version: number): Promise<MediaRestoreResult> {
    return this.http.post(`${this.prefix}/${mediaId}/restore`, { version });
  }

  /**
   * Upload bytes as a new artifact, or append a version when `mediaId` is set.
   * `bytes.type` is the file type and must be set.
   *
   * @param bytes The file itself (a File, a Blob, or raw bytes).
   * @param opts.label Artifact name; neutralized server-side.
   * @param opts.sessionId Optional session attribution.
   * @param opts.projectId Optional project attribution.
   * @param opts.mediaId Existing artifact to append a version to.
   */
  async upload(bytes: Blob, opts: MediaUploadParams): Promise<MediaRestoreResult> {
    if (bytes.type === '') {
      throw new SDKError(
        'upload: the blob carries no content type — construct it with an explicit type',
      );
    }
    const headers: Record<string, string> = {
      'X-Media-Label': encodeURIComponent(opts.label),
    };
    if (opts.sessionId) headers['X-Media-Session-Id'] = opts.sessionId;
    if (opts.projectId) headers['X-Media-Project-Id'] = opts.projectId;
    if (opts.mediaId) headers['X-Media-Id'] = opts.mediaId;
    return this.http.postBinary<MediaRestoreResult>(this.prefix, bytes, bytes.type, headers);
  }

  /** Soft-delete: move the artifact into Trash. */
  async delete(mediaId: string): Promise<void> {
    await this.http.delete(`${this.prefix}/${mediaId}`);
  }

  /** Restore a Trash item into the living library. */
  async undelete(mediaId: string): Promise<MediaObject> {
    return this.http.post(`${this.prefix}/${mediaId}/undelete`);
  }

  /** Hard-delete one Trash item immediately. */
  async purge(mediaId: string): Promise<void> {
    await this.http.post(`${this.prefix}/${mediaId}/purge`);
  }

  /** Hard-delete every Trash item for the caller account. */
  async emptyTrash(): Promise<void> {
    await this.http.post(`${this.prefix}/trash/empty`);
  }
}
